// src/repositories/supabase/SupabaseTransactionRepository.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ITransactionRepository, DBTransaction } from '../types';
import { requireAuth } from '../../services/supabaseClient';
import { TX_COLS } from '../../constants/queryColumns';
import { TX_QUERY_LIMIT } from '../../constants/limits';

/** Exported for reuse by BackupService.ts (raw exportBackup query, no repo method covers "all rows, all periods, anulada included"). */
export function mapRowToDBTransaction(row: Record<string, unknown>): DBTransaction {
  return {
    dbId:                    (row.db_id as number | null) ?? undefined,
    firmId:                  row.firm_id as string,
    companyId:               row.company_id as string,
    // id is stored as TEXT in PostgreSQL — must convert back to number
    id:                      Number(row.id),
    date:                    row.fecha as string,
    description:             row.descripcion as string,
    amount:                  Number(row.monto ?? 0),
    baseImponible:           row.base_imponible != null ? Number(row.base_imponible) : (row.monto != null ? Number(row.monto) : 0),
    igv:                     row.igv != null ? Number(row.igv) : 0,
    cuenta:                  (row.cuenta as string | null) ?? '639',
    nombreCuenta:            (row.nombre_cuenta as string | null) ?? '',
    moneda:                  (row.moneda as string | null) ?? 'PEN',
    status:                  ((row.status as string | null) ?? 'auto') as 'auto' | 'review' | 'approved',
    confidence:              (row.confidence as number | null) ?? 100,
    createdAt:               (row.created_at as string | null) ?? undefined,
    createdBy:               (row.created_by as string | null) ?? undefined,
    periodo:                 row.periodo as string,
    companyRuc:              (row.company_ruc as string | null) ?? undefined,
    rucProveedor:            (row.ruc_proveedor as string | null) ?? undefined,
    razonSocialProveedor:    (row.razon_social_proveedor as string | null) ?? undefined,
    igvRevision:             (row.igv_revision as boolean | null) ?? undefined,
    documentId:              (row.document_id as number | null) ?? undefined,
    serie:                   (row.serie as string | null) ?? undefined,
    correlativo:             (row.correlativo as string | null) ?? undefined,
    tipoDocumento:           (row.tipo_documento as string | null) ?? undefined,
    tipoDocumentoModificado: (row.tipo_documento_modificado as string | null) ?? undefined,
    anulada:                 (row.anulada as boolean | null) ?? undefined,
    detraccionFecha:         (row.detraccion_fecha as string | null) ?? undefined,
    detraccionNumConstancia: (row.detraccion_num_constancia as string | null) ?? undefined,
    codigoAfectacionIgv:     (row.codigo_afectacion_igv as string | null) ?? undefined,
    mappingSource:           ((row.mapping_source as string | null) ?? undefined) as DBTransaction['mappingSource'],
    rucCliente:              (row.ruc_cliente as string | null) ?? undefined,
    nombreCliente:           (row.nombre_cliente as string | null) ?? undefined,
    fechaVencimiento:        (row.fecha_vencimiento as string | null)       ?? undefined,
    fechaDocModificado:       (row.fecha_doc_modificado as string | null)       ?? undefined,
    serieDocModificado:       (row.serie_doc_modificado as string | null)       ?? undefined,
    correlativoDocModificado: (row.correlativo_doc_modificado as string | null) ?? undefined,
  };
}

export class SupabaseTransactionRepository implements ITransactionRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  async getByPeriod(firmId: string, companyId: string, periodo: string): Promise<DBTransaction[]> {
    await requireAuth();
    // Excluir anuladas e is_deleted: este repo alimenta SyncContext.liveTransactions
    // (Dashboard, EmprendedorDashboard, CxC, CxP, FinancialEngine.generarPyG).
    // Sin filtro `anulada`, los XML anulados detectados por SunatXmlParser
    // (commit d525804) inflarían totales aunque el flag se persista correctamente.
    const { data, error } = await this.client
      .from('transactions')
      .select(TX_COLS)
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('periodo', periodo)
      .neq('is_deleted', true)
      .neq('anulada', true);
    if (error) throw new Error(`[getByPeriod] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBTransaction);
  }

  async getByPeriodYTD(firmId: string, companyId: string, periodo: string): Promise<DBTransaction[]> {
    await requireAuth();
    const year = periodo.split('-')[0];
    const { data, error } = await this.client
      .from('transactions')
      .select(TX_COLS)
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .neq('is_deleted', true)
      .neq('anulada', true)
      .gte('periodo', `${year}-01`)
      .lte('periodo', periodo)
      .order('fecha', { ascending: true });
    if (error) throw new Error(`[getByPeriodYTD] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBTransaction);
  }

  async getByCompany(firmId: string, companyId: string, periodo?: string): Promise<DBTransaction[]> {
    await requireAuth();
    let query = this.client
      .from('transactions')
      .select(TX_COLS)
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .neq('is_deleted', true)
      .neq('anulada', true)
      .limit(TX_QUERY_LIMIT);
    if (periodo) query = query.eq('periodo', periodo);
    const { data, error } = await query;
    if (error) throw new Error(`[getByCompany] ${error.message}`);
    if (data && data.length >= TX_QUERY_LIMIT) {
      console.warn('⚠️ SupabaseTransactionRepository query hit limit — data may be truncated:', TX_QUERY_LIMIT);
    }
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBTransaction);
  }

  async countByPeriod(firmId: string, companyId: string, periodo: string): Promise<number> {
    await requireAuth();
    // Mismo filtro que getByPeriod para que count y data sean consistentes.
    const { count, error } = await this.client
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('periodo', periodo)
      .neq('is_deleted', true)
      .neq('anulada', true);
    if (error) throw new Error(`[countByPeriod] ${error.message}`);
    return count ?? 0;
  }
}
